import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, FileText, Landmark, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { contactInfo } from "../mock/mock";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contatto = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | ok | error
  const [nl, setNl] = useState("");
  const [nlConsent, setNlConsent] = useState(false);
  const [nlStatus, setNlStatus] = useState("idle");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, consent });
      setStatus("ok");
      setForm({ name: "", email: "", subject: "", message: "" });
      setConsent(false);
    } catch (err) {
      setStatus("error");
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!nl || !nlConsent) return;
    setNlStatus("sending");
    try {
      await axios.post(`${API}/newsletter`, { email: nl, consent: nlConsent });
      setNlStatus("ok");
      setNl("");
      setNlConsent(false);
    } catch (err) {
      setNlStatus("error");
    }
  };

  const info = [
    { icon: MapPin, label: t("contact.officeTitle"), value: t("contact.office") },
    { icon: Mail, label: t("contact.email"), value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: FileText, label: t("contact.pec"), value: contactInfo.pec, href: `mailto:${contactInfo.pec}` },
    { icon: Phone, label: t("contact.phone"), value: contactInfo.phone, href: `tel:${contactInfo.phone}` },
  ];

  return (
    <div>
      <PageHero
        image="https://static.wixstatic.com/media/0de1ea_a3fe5ab5b742407386392c8d79821097~mv2_d_4752_3168_s_4_2.jpg/v1/fill/w_1600,h_900,al_c,q_85,enc_auto/contatto.jpg"
        kicker="arts4all"
        title={t("contact.title")}
        lead={t("contact.lead")}
      />

      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14">
          {/* INFO */}
          <div>
            <Reveal className="space-y-7">
              {info.map((it, i) => {
                const Icon = it.icon;
                return (
                  <div key={i} className="flex gap-4">
                    <div className="w-11 h-11 rounded-full bg-white border border-[#eae4d9] flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-[#b5533a]" strokeWidth={1.6} />
                    </div>
                    <div>
                      <span className="text-xs uppercase tracking-[0.18em] text-[#8a8478]">
                        {it.label}
                      </span>
                      {it.href ? (
                        <a
                          href={it.href}
                          className="block text-[#1c1b1a] hover:text-[#b5533a] transition-colors whitespace-pre-line"
                        >
                          {it.value}
                        </a>
                      ) : (
                        <p className="text-[#1c1b1a] whitespace-pre-line">{it.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </Reveal>

            {/* Donations */}
            <Reveal delay={150} className="mt-10 bg-white border border-[#eae4d9] p-7">
              <div className="flex items-center gap-3">
                <Landmark className="w-5 h-5 text-[#b5533a]" />
                <h3 className="font-serif text-2xl text-[#1c1b1a]">{t("contact.donationsTitle")}</h3>
              </div>
              <p className="mt-2 text-sm text-[#6a655c]">{t("contact.donationsText")}</p>
              <div className="mt-5 space-y-1.5 text-sm text-[#3d3a35]">
                <p><span className="text-[#8a8478]">Banca:</span> {contactInfo.bank}</p>
                <p><span className="text-[#8a8478]">IBAN:</span> {contactInfo.iban}</p>
                <p><span className="text-[#8a8478]">SWIFT/BIC:</span> {contactInfo.swift}</p>
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <div>
            <Reveal className="bg-white border border-[#eae4d9] p-8 lg:p-10">
              <h3 className="font-serif text-3xl text-[#1c1b1a] mb-6">{t("contact.formTitle")}</h3>

              {status === "ok" ? (
                <div className="flex flex-col items-center text-center py-10">
                  <CheckCircle2 className="w-14 h-14 text-[#6b8e5a]" strokeWidth={1.4} />
                  <p className="mt-4 text-[#3d3a35]">{t("contact.success")}</p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-6 text-sm uppercase tracking-[0.14em] text-[#b5533a]"
                  >
                    OK
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] text-[#8a8478] mb-2">
                      {t("contact.name")}
                    </label>
                    <input
                      name="name"
                      required
                      value={form.name}
                      onChange={onChange}
                      className="w-full border border-[#dcd4c6] bg-[#faf7f2] px-4 py-3 text-[#1c1b1a] outline-none focus:border-[#b5533a] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] text-[#8a8478] mb-2">
                      {t("contact.emailField")}
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      className="w-full border border-[#dcd4c6] bg-[#faf7f2] px-4 py-3 text-[#1c1b1a] outline-none focus:border-[#b5533a] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] text-[#8a8478] mb-2">
                      {t("contact.subject")}
                    </label>
                    <input
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      className="w-full border border-[#dcd4c6] bg-[#faf7f2] px-4 py-3 text-[#1c1b1a] outline-none focus:border-[#b5533a] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.16em] text-[#8a8478] mb-2">
                      {t("contact.message")}
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={onChange}
                      className="w-full border border-[#dcd4c6] bg-[#faf7f2] px-4 py-3 text-[#1c1b1a] outline-none focus:border-[#b5533a] transition-colors resize-none"
                    />
                  </div>
                  {status === "error" && (
                    <p className="text-sm text-[#c0392b]">{t("contact.error")}</p>
                  )}
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      data-testid="contact-consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#b5533a] cursor-pointer"
                    />
                    <span className="text-xs leading-relaxed text-[#6a655c]">
                      {t("contact.consent")}{" "}
                      <Link
                        to="/cookie-policy"
                        className="text-[#b5533a] underline underline-offset-2 hover:text-[#a0472f]"
                      >
                        {t("contact.consentLink")}
                      </Link>
                      .
                    </span>
                  </label>
                  <button
                    type="submit"
                    disabled={status === "sending" || !consent}
                    data-testid="contact-submit-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#b5533a] hover:bg-[#a0472f] disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-sm uppercase tracking-[0.14em] transition-colors"
                  >
                    {status === "sending" ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> {t("contact.sending")}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> {t("contact.send")}
                      </>
                    )}
                  </button>
                </form>
              )}
            </Reveal>

            {/* Newsletter */}
            <Reveal delay={150} className="mt-8 bg-[#1c1b1a] p-8">
              <h3 className="font-serif text-2xl text-white">{t("contact.newsletterTitle")}</h3>
              <p className="mt-2 text-sm text-[#c7c0b4]">{t("contact.newsletterText")}</p>
              {nlStatus === "ok" ? (
                <p className="mt-4 inline-flex items-center gap-2 text-[#9dc088] text-sm">
                  <CheckCircle2 className="w-4 h-4" /> {t("contact.newsletterOk")}
                </p>
              ) : (
                <form onSubmit={subscribe} className="mt-5 space-y-4">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      required
                      value={nl}
                      onChange={(e) => setNl(e.target.value)}
                      placeholder="email@example.com"
                      data-testid="newsletter-email-input"
                      className="flex-1 bg-transparent border border-[#3a3733] px-4 py-3 text-white placeholder-[#7c766b] outline-none focus:border-[#e08a5f] transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={nlStatus === "sending" || !nlConsent}
                      data-testid="newsletter-submit-button"
                      className="bg-[#e08a5f] hover:bg-[#d1794e] disabled:opacity-60 disabled:cursor-not-allowed text-[#1c1b1a] px-6 py-3 text-sm uppercase tracking-[0.14em] font-medium transition-colors"
                    >
                      {t("contact.newsletterBtn")}
                    </button>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={nlConsent}
                      onChange={(e) => setNlConsent(e.target.checked)}
                      data-testid="newsletter-consent-checkbox"
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[#e08a5f] cursor-pointer"
                    />
                    <span className="text-xs leading-relaxed text-[#a49d90]">
                      {t("contact.newsletterConsent")}{" "}
                      <Link
                        to="/cookie-policy"
                        className="text-[#e08a5f] underline underline-offset-2 hover:text-[#f0a37c]"
                      >
                        {t("contact.newsletterConsentLink")}
                      </Link>
                      .
                    </span>
                  </label>
                  {nlStatus === "error" && (
                    <p className="text-xs text-[#f0a37c]">{t("contact.error")}</p>
                  )}
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contatto;
